import React, { Component } from "react";
import axiosInstance from "../../../axios-instance";
import Loader from "../../../components/Loader/Loader";
import { notification } from "../../../utils/utility";

const EMPTY_FORM = {
  name: "",
  description: "",
  credits: "",
  price: "",
  stripe_price_id: "",
  is_active: true,
  sort_order: 0,
};

const S = {
  page: { padding: "32px" },
  header: { marginBottom: 24 },
  title: { fontSize: 22, fontWeight: 700, color: "#111827", margin: 0 },
  subtitle: { fontSize: 14, color: "#6B7280", marginTop: 4 },
  card: {
    background: "#fff",
    border: "1px solid #E5E7EB",
    borderRadius: 12,
    padding: 24,
    marginBottom: 24,
  },
  sectionTitle: { fontSize: 16, fontWeight: 600, color: "#111827", marginBottom: 16 },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    textAlign: "left",
    padding: "10px 14px",
    background: "#F9FAFB",
    fontSize: 12,
    fontWeight: 600,
    color: "#6B7280",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    borderBottom: "1px solid #E5E7EB",
  },
  td: { padding: "12px 14px", fontSize: 14, color: "#374151", borderBottom: "1px solid #F3F4F6" },
  badge: (active) => ({
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: 9999,
    fontSize: 12,
    fontWeight: 600,
    background: active ? "#D1FAE5" : "#FEE2E2",
    color: active ? "#065F46" : "#991B1B",
  }),
  btn: (variant = "primary") => ({
    padding: "7px 16px",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    border: "none",
    background: variant === "primary" ? "#FE9920" : variant === "danger" ? "#EF4444" : "#F3F4F6",
    color: variant === "secondary" ? "#374151" : "#fff",
    marginRight: 6,
  }),
  formGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px 24px", marginBottom: 16 },
  label: { fontSize: 13, fontWeight: 600, color: "#374151", display: "block", marginBottom: 6 },
  input: {
    width: "100%",
    border: "1px solid #D1D5DB",
    borderRadius: 8,
    padding: "9px 12px",
    fontSize: 14,
    color: "#111827",
    outline: "none",
    boxSizing: "border-box",
  },
  hint: { fontSize: 12, color: "#9CA3AF", marginTop: 4 },
  formActions: { display: "flex", gap: 10, marginTop: 8 },
  emptyRow: { textAlign: "center", padding: "32px 0", color: "#9CA3AF", fontSize: 14 },
};

class SMSPackages extends Component {
  state = {
    packages: [],
    loading: true,
    showForm: false,
    isEdit: false,
    form: { ...EMPTY_FORM },
    saving: false,
    // Twilio pricing sync
    pricingStatus: null,
    syncing: false,
  };

  componentDidMount() {
    this.load();
    this.loadPricingStatus();
  }

  load = () => {
    this.setState({ loading: true });
    axiosInstance.get("super-admin/sms-packages")
      .then(res => this.setState({ packages: res.data?.data?.packages || [], loading: false }))
      .catch(() => { notification.error("Failed to load packages"); this.setState({ loading: false }); });
  };

  loadPricingStatus = () => {
    axiosInstance.get("super-admin/sms-packages/pricing-status")
      .then(res => this.setState({ pricingStatus: res.data?.data || null }))
      .catch(() => {}); // silent fail
  };

  syncTwilioPricing = () => {
    if (!window.confirm("Sync Twilio SMS pricing now? This will call the Twilio Pricing API and may take a moment.")) return;
    this.setState({ syncing: true });
    axiosInstance.post("super-admin/sms-packages/sync-twilio-pricing")
      .then(res => {
        notification.success("Twilio pricing sync complete!");
        this.setState({ syncing: false });
        this.loadPricingStatus();
      })
      .catch(err => {
        const msg = err?.response?.data?.message || "Sync failed. Check server logs.";
        notification.error(msg);
        this.setState({ syncing: false });
      });
  };

  openCreate = () => this.setState({ showForm: true, isEdit: false, form: { ...EMPTY_FORM } });
  openEdit = (pkg) => this.setState({
    showForm: true, isEdit: true,
    form: {
      id: pkg.id, name: pkg.name, description: pkg.description || "",
      credits: pkg.credits, price: pkg.price,
      stripe_price_id: pkg.stripe_price_id || "",
      is_active: pkg.is_active, sort_order: pkg.sort_order || 0,
    },
  });
  cancelForm = () => this.setState({ showForm: false, form: { ...EMPTY_FORM } });

  onChange = (e) => {
    const { name, value, type, checked } = e.target;
    this.setState(prev => ({ form: { ...prev.form, [name]: type === "checkbox" ? checked : value } }));
  };

  save = () => {
    const { form, isEdit } = this.state;
    if (!form.name || !form.credits || !form.price) {
      notification.error("Name, credits, and price are required."); return;
    }
    this.setState({ saving: true });
    const url = isEdit ? "super-admin/sms-packages/update" : "super-admin/sms-packages/create";
    axiosInstance.post(url, form)
      .then(() => {
        notification.success(isEdit ? "Package updated!" : "Package created!");
        this.setState({ showForm: false, saving: false });
        this.load();
      })
      .catch(err => {
        const msg = err?.response?.data?.message || "Failed to save.";
        notification.error(msg);
        this.setState({ saving: false });
      });
  };

  toggle = (pkg) => {
    axiosInstance.post("super-admin/sms-packages/toggle-status", { id: pkg.id })
      .then(() => { notification.success("Status updated"); this.load(); })
      .catch(() => notification.error("Failed to update status"));
  };

  delete = (pkg) => {
    if (!window.confirm(`Delete package "${pkg.name}"? This cannot be undone.`)) return;
    axiosInstance.post("super-admin/sms-packages/delete", { id: pkg.id })
      .then(() => { notification.success("Package deleted"); this.load(); })
      .catch(() => notification.error("Failed to delete package"));
  };

  render() {
    const { packages, loading, showForm, isEdit, form, saving, pricingStatus, syncing } = this.state;

    return (
      <div style={S.page}>
        <Loader isShowLoader={loading} />
        <div style={S.header}>
          <h2 style={S.title}>SMS Credit Packages</h2>
          <p style={S.subtitle}>
            Configure the SMS credit bundles admins can purchase for their team. Each package maps to a Stripe Price ID.
          </p>
        </div>

        {/* ── Twilio Pricing Sync Panel ─────────────────────────────── */}
        <div style={{ ...S.card, borderLeft: "4px solid #7C3AED" }}>
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
            <div>
              <h3 style={{ ...S.sectionTitle, margin: 0, color: "#7C3AED" }}>
                🔄 Twilio SMS Pricing Sync
              </h3>
              <p style={{ fontSize: 13, color: "#6B7280", marginTop: 6, marginBottom: 0, maxWidth: 520 }}>
                Sync current Twilio outbound SMS base rates for all supported destinations.
                Runs automatically on the <strong>1st of each month at 02:00</strong>.
                You can also trigger it manually below.
                1 credit = <strong>$0.0100</strong> effective rate.
              </p>
              {pricingStatus && (
                <p style={{ fontSize: 12, color: "#9CA3AF", marginTop: 8 }}>
                  <strong>Last synced:</strong>{" "}
                  {pricingStatus.last_synced
                    ? new Date(pricingStatus.last_synced).toLocaleString()
                    : "Never synced yet"}
                  {" · "}
                  <strong>Schedule:</strong> {pricingStatus.schedule}
                </p>
              )}
            </div>
            <button
              style={{
                ...S.btn("primary"),
                background: syncing ? "#D1D5DB" : "#7C3AED",
                cursor: syncing ? "not-allowed" : "pointer",
                whiteSpace: "nowrap",
              }}
              onClick={this.syncTwilioPricing}
              disabled={syncing}
            >
              {syncing ? "Syncing…" : "Sync Pricing Now"}
            </button>
          </div>
        </div>

        {showForm && (
          <div style={S.card}>
            <h3 style={S.sectionTitle}>{isEdit ? "Edit Package" : "Create Package"}</h3>
            <div style={S.formGrid}>
              <div>
                <label style={S.label}>Package Name *</label>
                <input style={S.input} name="name" value={form.name} onChange={this.onChange} placeholder="e.g. 500 SMS Credits" />
              </div>
              <div>
                <label style={S.label}>Credits *</label>
                <input style={S.input} type="number" name="credits" value={form.credits} onChange={this.onChange} placeholder="500" min="1" />
                <span style={S.hint}>Number of SMS credits included</span>
              </div>
              <div>
                <label style={S.label}>Price (USD) *</label>
                <input style={S.input} type="number" step="0.01" name="price" value={form.price} onChange={this.onChange} placeholder="9.99" min="0" />
              </div>
              <div>
                <label style={S.label}>Stripe Price ID</label>
                <input style={S.input} name="stripe_price_id" value={form.stripe_price_id} onChange={this.onChange} placeholder="price_1Abc..." />
                <span style={S.hint}>One-time Stripe Price ID (required to accept payment)</span>
              </div>
              <div>
                <label style={S.label}>Sort Order</label>
                <input style={S.input} type="number" name="sort_order" value={form.sort_order} onChange={this.onChange} placeholder="0" />
                <span style={S.hint}>Lower = shown first</span>
              </div>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <label style={S.label}>Description</label>
                <input style={S.input} name="description" value={form.description} onChange={this.onChange} placeholder="Optional description shown to admins" />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <input type="checkbox" id="is_active" name="is_active" checked={form.is_active} onChange={this.onChange} style={{ width: 18, height: 18, cursor: "pointer" }} />
                <label htmlFor="is_active" style={{ ...S.label, margin: 0, cursor: "pointer" }}>Active (visible to admins)</label>
              </div>
            </div>
            <div style={S.formActions}>
              <button style={S.btn("primary")} onClick={this.save} disabled={saving}>
                {saving ? "Saving..." : isEdit ? "Update Package" : "Create Package"}
              </button>
              <button style={S.btn("secondary")} onClick={this.cancelForm}>Cancel</button>
            </div>
          </div>
        )}

        <div style={S.card}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <h3 style={{ ...S.sectionTitle, margin: 0 }}>All Packages</h3>
            {!showForm && (
              <button style={S.btn("primary")} onClick={this.openCreate}>+ New Package</button>
            )}
          </div>

          {packages.length === 0 && !loading ? (
            <div style={S.emptyRow}>No packages yet. Create the first one above.</div>
          ) : (
            <table style={S.table}>
              <thead>
                <tr>
                  {["Name", "Credits", "Price", "Stripe Price ID", "Status", "Sort", "Actions"].map(h => (
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {packages.map(pkg => (
                  <tr key={pkg.id} style={{ background: pkg.is_active ? "#fff" : "#FAFAFA" }}>
                    <td style={S.td}>
                      <strong>{pkg.name}</strong>
                      {pkg.description && <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 2 }}>{pkg.description}</div>}
                    </td>
                    <td style={S.td}>{pkg.credits.toLocaleString()}</td>
                    <td style={S.td}>${Number(pkg.price).toFixed(2)}</td>
                    <td style={{ ...S.td, fontFamily: "monospace", fontSize: 12, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {pkg.stripe_price_id || <span style={{ color: "#F59E0B" }}>⚠ Not set</span>}
                    </td>
                    <td style={S.td}>
                      <span style={S.badge(pkg.is_active)}>{pkg.is_active ? "Active" : "Inactive"}</span>
                    </td>
                    <td style={S.td}>{pkg.sort_order}</td>
                    <td style={S.td}>
                      <button style={S.btn("secondary")} onClick={() => this.openEdit(pkg)}>Edit</button>
                      <button
                        style={S.btn(pkg.is_active ? "secondary" : "primary")}
                        onClick={() => this.toggle(pkg)}
                      >
                        {pkg.is_active ? "Deactivate" : "Activate"}
                      </button>
                      <button style={S.btn("danger")} onClick={() => this.delete(pkg)}>Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    );
  }
}

export default SMSPackages;
